import React, {
  useState,
  useCallback,
  useMemo,
} from 'react';

import { useApiQuery } from '../../hooks/useApiQuery';
import api from '../../services/api';

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import {
  CalendarIcon,
  ArrowPathIcon,
  CreditCardIcon,
  BuildingStorefrontIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  CheckIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

import useToast from '../../hooks/useToast';

// ============================================================
// VENDOR HELPERS
//   Returns null if the transaction has no valid vendor.
//   This is what removes "Unknown Vendor" cards.
// ============================================================
const getVendorKey = (t) => {
  if (!t || !t.vendorId) return null;

  // Populated object
  if (typeof t.vendorId === 'object' && t.vendorId?._id) {
    return String(t.vendorId._id);
  }

  // Raw string id (fallback when populate fails / string field)
  if (typeof t.vendorId === 'string' && t.vendorId.length > 0) {
    return t.vendorId;
  }

  return null;
};

const getVendorName = (t) => {
  if (!t) return 'Unknown';
  if (typeof t.vendorId === 'object' && t.vendorId?.name) {
    return t.vendorId.name;
  }
  if (t.vendorName) return t.vendorName;
  return 'Unknown Vendor';
};

const getVendorEmail = (t) => {
  if (!t) return '';
  if (typeof t.vendorId === 'object' && t.vendorId?.email) {
    return t.vendorId.email;
  }
  return '';
};

// ============================================================
// CUSTOM SELECT (vendor picker)
// ============================================================
const VendorSelect = ({ value, onChange, options, disabled }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} className="relative min-w-[220px]">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between border rounded-lg px-3 py-2 text-sm text-left transition-all ${
          disabled
            ? 'opacity-50 cursor-not-allowed border-gray-200'
            : open
            ? 'border-[#1672b9] ring-2 ring-[#1672b9]/20 bg-white'
            : 'border-gray-200 bg-white hover:border-[#1672b9]/50'
        }`}
      >
        <span className="flex items-center gap-2 truncate">
          <BuildingStorefrontIcon className="w-4 h-4 text-[#1672b9] shrink-0" />
          <span className={selected ? 'text-gray-900 truncate' : 'text-gray-400'}>
            {selected ? selected.label : 'Select vendor'}
          </span>
        </span>
        <ChevronDownIcon
          className={`w-4 h-4 text-gray-400 transition-transform ${
            open ? 'rotate-180 text-[#1672b9]' : ''
          }`}
        />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-lg border border-gray-200 shadow-lg py-1 max-h-64 overflow-y-auto">
          {options.map((opt) => {
            const isSel = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left transition-colors ${
                  isSel
                    ? 'bg-[#1672b9]/8 text-[#1672b9] font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="truncate">{opt.label}</span>
                {isSel && <CheckIcon className="w-4 h-4 text-[#1672b9]" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const WalletReports = () => {
  const { showError } = useToast();

  // ============================================================
  // DATE FILTERS
  // ============================================================
  const getDefaultStartDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  };
  const getToday = () => new Date().toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getToday());
  const [filterKey, setFilterKey] = useState(0);

  // 'all' = aggregate; otherwise the vendor's _id
  const [selectedVendor, setSelectedVendor] = useState('all');

  // ============================================================
  // FETCH TRANSACTIONS
  // ============================================================
  const {
    data: transactions = [],
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useApiQuery(
    ['wallet-transactions', startDate, endDate, filterKey],
    async () => {
     const response = await api.get('/wallet/vendor-collections', {
  params: { startDate, endDate, page: 1, limit: 500 },
});
      const responseData = response.data;
      if (Array.isArray(responseData)) return responseData;
      if (Array.isArray(responseData?.transactions)) {
        return responseData.transactions;
      }
      return [];
    },
    {
      staleTime: 5 * 60 * 1000,
      onError: (err) => console.error('Wallet transaction error:', err),
    },
  );

  // ============================================================
  // FILTER HANDLERS
  // ============================================================
  const handleFilter = useCallback(
    (e) => {
      e.preventDefault();
      if (!startDate || !endDate) {
        showError?.('Please select both dates');
        return;
      }
      if (new Date(startDate) > new Date(endDate)) {
        showError?.('Start date cannot be after end date');
        return;
      }
      setFilterKey((prev) => prev + 1);
    },
    [startDate, endDate, showError],
  );

  const handleReset = useCallback(() => {
    setStartDate(getDefaultStartDate());
    setEndDate(getToday());
    setSelectedVendor('all');
    setFilterKey((prev) => prev + 1);
  }, []);

  const handleRefresh = useCallback(() => refetch(), [refetch]);

  // ============================================================
  // FORMAT
  // ============================================================
  const formatCurrency = useCallback((amount) => {
    const n = Number(amount) || 0;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(n);
  }, []);

  // ============================================================
  // PROCESS
  //   - Only CREDIT transactions with a valid vendor
  //     count toward the vendor breakdown.
  //   - vendorSummary = [{ key, name, email, collected, count, transactions }]
  //   - selectedVendorData = details for the selected vendor (or null)
  // ============================================================
  const {
    vendorSummary,
    vendorOptions,
    filteredTransactions,
    selectedVendorData,
    summary,
    chartData,
  } = useMemo(() => {
    const safe = Array.isArray(transactions) ? transactions : [];

    // -------- 1. Keep only vendor credits (money the vendor received) --------
    const vendorCredits = safe.filter((t) => {
      if (t.type !== 'credit') return false;
      if (!getVendorKey(t)) return false;
      return true;
    });

    // -------- 2. Group by vendor --------
    const byVendor = {};

    vendorCredits.forEach((t) => {
      const key = getVendorKey(t);
      const name = getVendorName(t);
      const email = getVendorEmail(t);
      const amount = Number(t.amount) || 0;

      if (!byVendor[key]) {
        byVendor[key] = {
          key,
          name,
          email,
          collected: 0,
          count: 0,
          transactions: [],
        };
      }

      byVendor[key].collected += amount;
      byVendor[key].count += 1;
      byVendor[key].transactions.push(t);
    });

    const vendorSummary = Object.values(byVendor).sort(
      (a, b) => b.collected - a.collected,
    );

    const vendorOptions = [
      { value: 'all', label: `All Vendors (${vendorSummary.length})` },
      ...vendorSummary.map((v) => ({ value: v.key, label: v.name })),
    ];

    // -------- 3. Data for the selected scope --------
    const selectedVendorData =
      selectedVendor === 'all'
        ? null
        : vendorSummary.find((v) => v.key === selectedVendor) || null;

    const filteredTransactions =
      selectedVendor === 'all'
        ? vendorCredits
        : selectedVendorData?.transactions || [];

    // -------- 4. Summary numbers for the current scope --------
    const totalCollected = filteredTransactions.reduce(
      (sum, t) => sum + (Number(t.amount) || 0),
      0,
    );

    const summary = {
      totalCollected,
      totalTransactions: filteredTransactions.length,
      vendorCount:
        selectedVendor === 'all'
          ? vendorSummary.length
          : selectedVendorData
          ? 1
          : 0,
      avgPerVendor:
        vendorSummary.length > 0
          ? totalCollected /
            (selectedVendor === 'all'
              ? vendorSummary.length
              : 1)
          : 0,
    };

    // -------- 5. Daily chart (credits only) --------
    const grouped = {};
    filteredTransactions.forEach((t) => {
      if (!t.createdAt) return;
      const date = new Date(t.createdAt).toISOString().split('T')[0];
      if (!grouped[date]) grouped[date] = { date, collected: 0 };
      grouped[date].collected += Number(t.amount) || 0;
    });

    const chartData = Object.values(grouped).sort((a, b) =>
      a.date > b.date ? 1 : -1,
    );

    return {
      vendorSummary,
      vendorOptions,
      filteredTransactions,
      selectedVendorData,
      summary,
      chartData,
    };
  }, [transactions, selectedVendor]);

  const tooltipStyle = {
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    fontSize: '12px',
    color: '#111827',
    padding: '8px 12px',
  };

  // ============================================================
  // LOADING / ERROR
  // ============================================================
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-gray-500 text-sm">Loading report data...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-10 text-[#708090]">
        <p className="text-red-500">Failed to load report data</p>
        <button
          onClick={handleRefresh}
          className="mt-4 text-[#1471b9] hover:underline"
        >
          Try Again
        </button>
        {error?.message && (
          <p className="text-xs text-gray-400 mt-2">{error.message}</p>
        )}
      </div>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="space-y-6">
      {/* ============ HEADER ============ */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Vendor Wallet Reports
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Money each vendor collected from attendees
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isFetching}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-[#1672b9] transition-all disabled:opacity-50"
        >
          <ArrowPathIcon
            className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`}
          />
          {isFetching ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* ============ FILTERS ============ */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <form
          onSubmit={handleFilter}
          className="flex flex-wrap items-end gap-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vendor
            </label>
            <VendorSelect
              value={selectedVendor}
              onChange={setSelectedVendor}
              options={vendorOptions}
              disabled={isFetching}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={isFetching}
              className="bg-[#1672b9] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0f5a9e] transition-all disabled:opacity-50"
            >
              {isFetching ? 'Loading...' : 'Apply Filter'}
            </button>
            <button
              type="button"
              onClick={handleReset}
              disabled={isFetching}
              className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-all disabled:opacity-50"
            >
              Reset
            </button>
          </div>
        </form>
      </div>

      {/* ============ VENDOR BREAKDOWN / DETAIL ============ */}
      {vendorSummary.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
          <BuildingStorefrontIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">
            No vendor transactions in this period.
          </p>
        </div>
      ) : selectedVendor === 'all' ? (
        /* ---------- ALL VENDORS GRID ---------- */
        <div>
          <h2 className="text-base font-medium text-gray-900 mb-3">
            Vendor Breakdown
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {vendorSummary.map((v) => (
              <button
                key={v.key}
                onClick={() => setSelectedVendor(v.key)}
                className="text-left bg-white rounded-xl border border-gray-100 shadow-sm p-4 transition-all hover:shadow-md hover:border-[#1672b9]/40"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 bg-blue-50 rounded-md">
                    <BuildingStorefrontIcon className="w-4 h-4 text-[#1672b9]" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 truncate">
                    {v.name}
                  </span>
                </div>

                <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
                  Collected
                </p>
                <p className="text-xl font-bold text-green-700">
                  {formatCurrency(v.collected)}
                </p>

                <div className="text-[10px] text-gray-400 mt-2">
                  {v.count} transaction{v.count !== 1 ? 's' : ''}
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        /* ---------- SINGLE VENDOR DETAIL ---------- */
        <div className="space-y-4">
          <button
            onClick={() => setSelectedVendor('all')}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#1672b9] transition-colors"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            Back to all vendors
          </button>

          {selectedVendorData && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-50 rounded-xl">
                    <BuildingStorefrontIcon className="w-6 h-6 text-[#1672b9]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedVendorData.name}
                    </h2>
                    {selectedVendorData.email && (
                      <p className="text-sm text-gray-500 mt-1 flex items-center gap-1.5">
                        <EnvelopeIcon className="w-4 h-4" />
                        {selectedVendorData.email}
                      </p>
                    )}
                  </div>
                </div>

                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
                    Total Collected
                  </p>
                  <p className="text-3xl font-bold text-green-700">
                    {formatCurrency(selectedVendorData.collected)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {selectedVendorData.count} transaction
                    {selectedVendorData.count !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============ SCOPE LABEL ============ */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <span>Showing:</span>
        <span className="font-semibold text-[#1672b9]">
          {vendorOptions.find((o) => o.value === selectedVendor)?.label ||
            'All Vendors'}
        </span>
      </div>

      {/* ============ SUMMARY CARDS ============ */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <MetricCard
          title="Total Collected"
          value={formatCurrency(summary.totalCollected)}
          icon={<CreditCardIcon className="w-5 h-5 text-green-600" />}
        />
        <MetricCard
          title={
            selectedVendor === 'all' ? 'Vendors' : 'Vendor'
          }
          value={summary.vendorCount}
          icon={
            <BuildingStorefrontIcon className="w-5 h-5 text-[#1672b9]" />
          }
        />
        <MetricCard
          title="Transactions"
          value={summary.totalTransactions}
          icon={<CalendarIcon className="w-5 h-5 text-[#1672b9]" />}
        />
      </div>

    

      {/* ============ TRANSACTION LIST ============ */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <h3 className="text-base font-medium text-gray-900 mb-4">
          {selectedVendor === 'all'
            ? 'Recent Vendor Collections'
            : `All Transactions — ${selectedVendorData?.name || ''}`}
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Vendor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Attendee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Description
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center py-8 text-gray-500"
                  >
                    No transactions found.
                  </td>
                </tr>
              ) : (
                (selectedVendor === 'all'
                  ? filteredTransactions.slice(0, 20)
                  : filteredTransactions
                ).map((t) => (
                  <tr key={t._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {getVendorName(t)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {t.attendeeId?.name ||
                        t.userId?.name ||
                        'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-700">
                      {formatCurrency(t.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {t.createdAt
                        ? new Date(
                            t.createdAt,
                          ).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {t.description || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {selectedVendor === 'all' &&
            filteredTransactions.length > 20 && (
              <p className="text-xs text-gray-400 text-center mt-3">
                Showing 20 of {filteredTransactions.length} transactions.
                Select a vendor for the complete list.
              </p>
            )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// METRIC CARD
// ============================================================
const MetricCard = React.memo(({ title, value, icon }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 transition-all hover:shadow-md">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
          {title}
        </p>
        <p className="text-xl font-semibold text-gray-900 mt-1">
          {value}
        </p>
      </div>
      <div className="p-2 bg-gray-50 rounded-full">{icon}</div>
    </div>
  </div>
));

MetricCard.displayName = 'MetricCard';

export default WalletReports;